import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode
} from '../types'
import { addColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class H3FromPoint extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.h3frompoint',
        group: 'Spatial Indexes',
        title: 'H3 from GeoPoint',
        description: `
      This component adds a new column containing the indices of H3 cells corresponding to points.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'pointscol',
          type: WorkflowComponentParamType.Column,
          title: 'Points column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'resolution',
          type: WorkflowComponentParamType.Number,
          title: 'Resolution',
          min: 0,
          max: 15,
          default: 8,
          mode: WorkflowComponentParameterMode.Slider
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)

    let select = ''
    if (connection.type === Provider.DatabricksRest) {
      select = dedent`SELECT /*+ REPARTITION(40) */
        H3_POINTASH3STRING(
          ${preparedParams.pointscol as string}, CAST(${preparedParams.resolution} AS INTEGER)
        ) h3, *
      FROM ${connection.quoteName(preparedParams.source as string)}`
    } else {
      select = dedent`SELECT
      ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.H3_FROMGEOGPOINT(
          ${preparedParams.pointscol}, CAST(${preparedParams.resolution} AS INTEGER)
        ) h3, *
      FROM ${connection.quoteName(preparedParams.source as string)}`
    }

    let query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    if (connection.type === Provider.Postgres) {
      query = dedent`IF EXISTS(SELECT 1 FROM pg_extension WHERE extname = 'plv8') THEN
                        ${query}
                      ELSE
                        RAISE EXCEPTION 'plv8 extension not installed';
                      END IF;`
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.1.1'
      default:
        return '1.0.0'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    return addColumn(columns.source, {
      name: `h3`,
      type: ColumnDataType.String
    })
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Postgres, Provider.DatabricksRest]
  }
}

export { H3FromPoint }
